import { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles, Instagram, Linkedin, Facebook, Plus } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { CookieConsent } from "@/components/CookieConsent";
import { SEO } from "@/components/SEO";
import { SERVICES, PILLARS } from "@/lib/brand";
import api from "@/lib/api";

const SLIDES = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "process", label: "Process" },
  { id: "contact", label: "Contact" },
];

const PROCESS = [
  { n: "01", title: "Discovery", desc: "We listen first — an unhurried conversation to understand your goals, your people, and what success looks like for you." },
  { n: "02", title: "Strategy", desc: "A tailored plan grounded in practical, PHR-certified expertise and the nuances of your unique context." },
  { n: "03", title: "Delivery", desc: "Hands-on execution, fully virtual, with clear milestones and a refined, considered craft at every step." },
  { n: "04", title: "Partnership", desc: "Ongoing support that evolves with you — intentional, boutique, and always people-first." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 1.05, delay: 0.12 * i, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const containerRef = useRef(null);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { root, threshold: 0.55 }
    );
    root.querySelectorAll("[data-slide]").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const go = useCallback((id) => {
    containerRef.current?.querySelector(`#${id}`)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const [email, setEmail] = useState("");
  const [activeStep, setActiveStep] = useState(null);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try { await api.post("/newsletter", { email }); toast.success("You're subscribed. Welcome to the circle."); setEmail(""); }
    catch { toast.error("Please try again."); }
  };

  const vp = { amount: 0.4, root: containerRef, once: false };

  return (
    <div className="relative h-screen overflow-hidden bg-ivory">
      <SEO title="People First. Possibilities Redefined." description="A luxury boutique HR consulting, recruiting, and career-services experience — elevated, intentional, 100% virtual." />
      <Navbar />
      <CookieConsent />

      {/* Slide dot navigation */}
      <nav className="hidden md:flex flex-col gap-4 fixed right-8 top-1/2 -translate-y-1/2 z-40" aria-label="Section navigation" data-testid="slide-nav">
        {SLIDES.map((s) => (
          <button
            key={s.id}
            onClick={() => go(s.id)}
            data-testid={`slide-dot-${s.id}`}
            className="group flex items-center justify-end gap-3"
            aria-label={`Go to ${s.label}`}
          >
            <span className={`text-xs tracking-[0.2em] uppercase transition-all duration-500 ${active === s.id ? "opacity-100 text-espresso" : "opacity-0 group-hover:opacity-70 text-espresso/70"}`}>
              {s.label}
            </span>
            <span className={`rounded-full transition-all duration-500 ${active === s.id ? "w-3 h-3 bg-gold" : "w-2 h-2 bg-champagne group-hover:bg-gold/60"}`} />
          </button>
        ))}
      </nav>

      <div
        ref={containerRef}
        data-testid="slides-container"
        className="h-screen overflow-y-scroll snap-y snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: "none" }}
      >
        {/* SLIDE 1 — HOME */}
        <section id="home" data-slide data-testid="slide-home" className="h-screen w-full snap-start relative flex items-center overflow-hidden">
          <div className="w-full max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pt-24 lg:pt-0">
            <div className="max-w-2xl">
              <motion.span custom={0} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
                className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-gold font-medium">
                <Sparkles size={14} /> rethinking hr. redefining opportunity.
              </motion.span>
              <motion.h1 custom={1} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
                className="font-serif text-6xl md:text-8xl leading-[0.98] text-espresso mt-8">
                People First.<br /><span className="italic text-champagne">Possibilities</span><br />Redefined.
              </motion.h1>
              <motion.p custom={2} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
                className="mt-8 text-lg text-espresso/75 max-w-md leading-relaxed">
                Elevated HR solutions, intentional talent strategies, and personalized career services — for businesses that thrive and professionals who move forward with confidence.
              </motion.p>
              <motion.div custom={3} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
                className="flex flex-col sm:flex-row gap-4 mt-10">
                <button onClick={() => go("services")} data-testid="hero-explore-services"
                  className="inline-flex items-center justify-center gap-2 bg-espresso text-ivory rounded-full px-8 py-4 font-medium hover:bg-forest transition-colors duration-300">
                  Explore Our Services <ArrowRight size={18} />
                </button>
                <Link to="/book" data-testid="hero-book-consultation"
                  className="inline-flex items-center justify-center gap-2 border border-espresso text-espresso rounded-full px-8 py-4 font-medium hover:bg-espresso hover:text-ivory transition-colors duration-300">
                  Book a Consultation
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SLIDE 2 — ABOUT */}
        <section id="about" data-slide data-testid="slide-about" className="h-screen w-full snap-start relative flex items-center bg-taupe/60">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
            <motion.span custom={0} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
              className="text-xs uppercase tracking-[0.3em] text-gold font-medium">A boutique practice</motion.span>
            <motion.h2 custom={1} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
              className="font-serif text-4xl md:text-6xl text-espresso mt-5 leading-[1.05]">
              A more intentional approach to people &amp; talent.
            </motion.h2>
            <motion.p custom={2} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
              className="mt-8 text-lg md:text-xl text-espresso/75 leading-relaxed">
              Founded by an MBA-educated, PHR-certified HR professional, people, redefined bridges business growth and professional development through personalized, flexible, people-centered solutions.
            </motion.p>
            <motion.p custom={3} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
              className="mt-5 text-lg text-espresso/70 leading-relaxed">
              Thoughtful service. Practical expertise. Confidentiality. And the belief that people sit at the center of every successful business.
            </motion.p>
            <motion.div custom={4} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp} className="mt-10 flex flex-wrap gap-x-10 gap-y-4">
              {PILLARS.map((p) => (
                <div key={p.title} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-champagne" />
                  <span className="text-espresso font-medium text-sm">{p.title}</span>
                </div>
              ))}
            </motion.div>
            <motion.div custom={5} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp} className="mt-10">
              <Link to="/about" data-testid="about-read-more" className="inline-flex items-center gap-2 text-espresso font-medium hover:text-champagne transition-colors">
                Read our full story <ArrowUpRight size={18} />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* SLIDE 3 — SERVICES */}
        <section id="services" data-slide data-testid="slide-services" className="h-screen w-full snap-start relative flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 w-full">
            <motion.div custom={0} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp} className="mb-10">
              <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">What we do</span>
              <h2 className="font-serif text-4xl md:text-6xl text-espresso mt-3">Three ways we help.</h2>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-10 lg:gap-16">
              {SERVICES.map((s, i) => (
                <motion.div key={s.key} custom={i + 1} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
                  data-testid={`service-card-${s.key}`}
                  className="group flex flex-col border-t border-espresso/15 pt-8">
                  <span className="text-xs uppercase tracking-[0.25em] text-champagne">{s.tag}</span>
                  <h3 className="font-serif text-3xl md:text-4xl text-espresso mt-5">{s.title}</h3>
                  <p className="text-espresso/70 mt-5 leading-relaxed flex-1">{s.blurb}</p>
                  <Link to={s.to} data-testid={`service-explore-${s.key}`}
                    className="inline-flex items-center gap-2 mt-8 text-espresso font-medium group-hover:text-champagne transition-colors">
                    Explore Services <ArrowUpRight size={18} />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SLIDE 4 — PROCESS */}
        <section id="process" data-slide data-testid="slide-process" className="h-screen w-full snap-start relative flex items-center bg-ivory">
          <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24 w-full">
            <motion.div custom={0} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp} className="mb-10">
              <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">How we work</span>
              <h2 className="font-serif text-4xl md:text-6xl text-espresso mt-3">An unhurried, considered process.</h2>
            </motion.div>
            <div className="border-b border-espresso/15">
              {PROCESS.map((p, i) => {
                const open = activeStep === i;
                return (
                  <motion.div
                    key={p.n}
                    custom={i + 1}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={vp}
                    data-testid={`process-step-${p.n}`}
                    onMouseEnter={() => setActiveStep(i)}
                    onMouseLeave={() => setActiveStep((a) => (a === i ? null : a))}
                    onClick={() => { if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(hover: none)").matches) setActiveStep((a) => (a === i ? null : i)); }}
                    onFocus={() => setActiveStep(i)}
                    tabIndex={0}
                    role="button"
                    aria-expanded={open}
                    className="group border-t border-espresso/15 py-7 cursor-pointer outline-none"
                  >
                    <div className="flex items-baseline gap-5 md:gap-8">
                      <span className={`font-serif text-2xl md:text-3xl w-10 shrink-0 transition-colors duration-500 ${open ? "text-champagne" : "text-espresso/35"}`}>{p.n}</span>
                      <h3 className={`font-serif text-2xl md:text-4xl transition-colors duration-500 ${open ? "text-champagne" : "text-espresso"}`}>{p.title}</h3>
                      <Plus size={22} className={`ml-auto self-center shrink-0 transition-all duration-500 ${open ? "rotate-45 text-champagne" : "text-espresso/40"}`} />
                    </div>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          key="desc"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <p className="pt-4 md:pl-[3.75rem] max-w-2xl text-espresso/70 text-base md:text-lg leading-relaxed" data-testid={`process-desc-${p.n}`}>
                            {p.desc}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
            <motion.p custom={6} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp} className="text-espresso/45 text-sm mt-6 italic">
              Hover or tap each step to explore.
            </motion.p>
          </div>
        </section>

        {/* SLIDE 5 — CONTACT */}
        <section id="contact" data-slide data-testid="slide-contact" className="h-screen w-full snap-start relative flex items-center bg-espresso overflow-hidden">
          <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 w-full relative z-10 text-center">
            <motion.h2 custom={0} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
              className="font-serif text-4xl md:text-6xl text-ivory leading-[1.05]">Your next chapter starts here.</motion.h2>
            <motion.p custom={1} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
              className="text-ivory/75 text-lg mt-5 max-w-2xl mx-auto">
              Whether you're growing a business or growing your career, let's create something meaningful.
            </motion.p>
            <motion.div custom={2} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
              className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link to="/book" data-testid="contact-book" className="bg-champagne text-espresso rounded-full px-8 py-4 font-medium hover:bg-ivory transition-colors duration-300">Book a Consultation</Link>
              <Link to="/for-businesses" data-testid="contact-work-with-us" className="border border-ivory/40 text-ivory rounded-full px-8 py-4 font-medium hover:bg-ivory hover:text-espresso transition-colors duration-300">Work With Us</Link>
              <Link to="/for-professionals" data-testid="contact-career" className="border border-ivory/40 text-ivory rounded-full px-8 py-4 font-medium hover:bg-ivory hover:text-espresso transition-colors duration-300">Career Services</Link>
            </motion.div>

            <motion.form custom={3} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp}
              onSubmit={subscribe} data-testid="slide-newsletter-form" className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mt-8">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="slide-newsletter-input"
                className="flex-1 bg-ivory/10 border border-ivory/20 rounded-full px-5 py-3 text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-champagne" />
              <button type="submit" data-testid="slide-newsletter-submit" className="bg-champagne text-espresso rounded-full px-6 py-3 font-medium hover:bg-ivory transition-colors">Join</button>
            </motion.form>

            <motion.div custom={4} variants={fadeUp} initial="hidden" whileInView="show" viewport={vp} className="flex justify-center gap-4 mt-8">
              <a href="#" aria-label="Instagram" className="text-ivory/70 hover:text-champagne transition-colors"><Instagram size={20} /></a>
              <a href="#" aria-label="LinkedIn" className="text-ivory/70 hover:text-champagne transition-colors"><Linkedin size={20} /></a>
              <a href="#" aria-label="Facebook" className="text-ivory/70 hover:text-champagne transition-colors"><Facebook size={20} /></a>
            </motion.div>

            <div className="border-t border-ivory/15 mt-8 pt-6 flex flex-col md:flex-row justify-between items-center gap-3 text-sm text-ivory/55">
              <p>© {new Date().getFullYear()} people, redefined. · Amani Talent Agency LLC</p>
              <div className="flex gap-6">
                <Link to="/contact" className="hover:text-ivory transition-colors">Contact</Link>
                <Link to="/legal/privacy" className="hover:text-ivory transition-colors">Privacy</Link>
                <Link to="/legal/terms" className="hover:text-ivory transition-colors">Terms</Link>
                <Link to="/legal/refund" className="hover:text-ivory transition-colors">Refund</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
